import { useEffect, useMemo, useState } from 'react';
import { tmdbService } from '../services/tmdbApi';
import { hasUsableCrewPoster, isLikelyPlaceholderCrewTitle } from '../utils/crewActivityMovieDisplay';

type TmdbMovieShape = {
  title?: string;
  Title?: string;
  poster_path?: string | null;
  Poster?: string;
  release_date?: string;
  Year?: string;
  genres?: Array<{ name?: string }>;
};

/**
 * Fills poster (and placeholder title/year/genre) from TMDB when activity metadata is incomplete.
 */
export function useCrewActivityMovieDisplay(
  movieId: string | undefined,
  storedPoster: string | undefined | null,
  storedTitle: string | undefined,
  storedYear: string | undefined,
  storedGenre: string | undefined
): {
  poster: string | undefined;
  title: string;
  year: string | undefined;
  genre: string | undefined;
} {
  const [extra, setExtra] = useState<{
    poster?: string;
    title?: string;
    year?: string;
    genre?: string;
  }>({});

  const shouldFetch = useMemo(() => {
    if (!movieId) return false;
    const needPoster = !hasUsableCrewPoster(storedPoster);
    const needTitle = isLikelyPlaceholderCrewTitle(storedTitle, movieId);
    return needPoster || needTitle;
  }, [movieId, storedPoster, storedTitle]);

  useEffect(() => {
    if (!shouldFetch || !movieId) return;
    let cancelled = false;
    setExtra({});
    tmdbService
      .getMovieById(movieId)
      .then((m: TmdbMovieShape) => {
        if (cancelled || !m) return;
        const pathOrUrl = m.poster_path || (m.Poster && m.Poster !== 'N/A' ? m.Poster : undefined);
        const y = m.release_date?.split('-')[0] || m.Year;
        const g = Array.isArray(m.genres) ? m.genres[0]?.name : undefined;
        setExtra({
          poster: pathOrUrl || undefined,
          title: m.title || m.Title,
          year: y || undefined,
          genre: g || undefined,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [shouldFetch, movieId]);

  const poster = hasUsableCrewPoster(storedPoster) ? String(storedPoster).trim() : extra.poster;

  const title =
    !isLikelyPlaceholderCrewTitle(storedTitle, movieId) && storedTitle
      ? storedTitle
      : extra.title || storedTitle || 'Film';

  const year = storedYear || extra.year;
  const genre = storedGenre || extra.genre;

  return { poster, title, year, genre };
}
